import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { mapTo, switchMap, tap } from 'rxjs/operators';
import _get from 'lodash-es/get';
import { ProductConfiguration } from '@shared/core/types';
import { ProductConfigApiService } from './product-config-api.service';
import { EServiceTypeKey } from '@skyeng/libs/shared/stk';
import { TranslocoService } from '@ngneat/transloco';

@Injectable({
  providedIn: 'root',
})
export class ProductConfigService {
  private configsMap: Map<EServiceTypeKey, ProductConfiguration> = new Map<EServiceTypeKey, ProductConfiguration>();

  constructor(private productConfigApi: ProductConfigApiService, private translocoService: TranslocoService) {}

  loadConfig(): Observable<boolean> {
    if (!this.isConfigLoaded()) {
      return this.productConfigApi.getProductConfigList().pipe(
        switchMap(configs => {
          if (configs.length === 0) {
            window.alert(
              this.translocoService.translate('sharedModule.productConfigService.failedToLoadConfiguration'),
            );
            window.location.reload();
            throwError(new Error('No product config loaded'));
          }

          return of(configs);
        }),
        tap(configs => this.setConfigs(configs)),
        mapTo(true),
      );
    }

    return of(true);
  }

  getAll(): ReadonlyMap<EServiceTypeKey, ProductConfiguration> {
    if (!this.isConfigLoaded()) {
      throw new Error('ProductConfigService: load product config first. `ProductConfigService.loadConfig()`');
    }
    return this.configsMap;
  }

  getByStk(stk: EServiceTypeKey): ProductConfiguration | undefined {
    return this.getAll().get(stk);
  }

  getFieldByStk<TKey extends keyof ProductConfiguration>(
    stk: EServiceTypeKey | string,
    fieldPath: TKey | [TKey],
    defaultValue?: any,
  ): ProductConfiguration[TKey] | undefined | null;
  getFieldByStk<TKey extends keyof ProductConfiguration, TKey2 extends keyof ProductConfiguration[TKey]>(
    stk: EServiceTypeKey,
    fieldPath: [TKey, TKey2],
    defaultValue?: any,
  ): ProductConfiguration[TKey][TKey2] | undefined | null;
  getFieldByStk<TKey extends keyof ProductConfiguration, TKey2 extends keyof ProductConfiguration[TKey]>(
    stk: EServiceTypeKey,
    fieldPath: string,
    defaultValue?: any,
  ): ProductConfiguration[TKey] | ProductConfiguration[TKey][TKey2] | any | undefined | null;
  getFieldByStk(stk: EServiceTypeKey, fieldPath: any, defaultValue?: any): any | undefined | null {
    return _get(this.getByStk(stk), fieldPath, defaultValue);
  }

  private isConfigLoaded(): boolean {
    return this.configsMap.size > 0;
  }

  private setConfigs(config: ProductConfiguration[]): void {
    config.forEach((conf: ProductConfiguration) => {
      this.configsMap.set(conf.serviceTypeKey, conf);
    });
  }
}
